import { create } from 'zustand';

import type { UserRole } from '@novapet/shared';

import { useBranchStore } from './branchStore';

export interface AuthUser {
  readonly id: string;
  readonly email: string;
  readonly full_name: string;
  readonly roles: ReadonlyArray<UserRole>;
  readonly permissions: ReadonlyArray<string>;
}

interface AuthState {
  access_token: string | null;
  user: AuthUser | null;
  is_locked: boolean;
  setSession: (access_token: string, user: AuthUser) => void;
  clearSession: () => void;
  lockSession: () => void;
  unlockSession: () => void;
}

/**
 * Auth state lives in memory only — refresh token is in an httpOnly cookie set by the
 * API. `is_locked` powers the inactivity overlay; doctor drafts persist independently
 * (see `prompts/02_DEVELOPMENT_RULES_EN.md` §28).
 */
export const useAuthStore = create<AuthState>((set) => ({
  access_token: null,
  user: null,
  is_locked: false,
  setSession: (access_token, user) => set({ access_token, user, is_locked: false }),
  clearSession: () => {
    // Clear the admin branch selection on EVERY session end (logout, refresh failure, revoke),
    // not just the explicit logout button — covers the apolloClient silent-refresh path too.
    useBranchStore.getState().clearSelectedBranch();
    set({ access_token: null, user: null, is_locked: false });
  },
  lockSession: () => set({ is_locked: true }),
  unlockSession: () => set({ is_locked: false }),
}));
